/**
 * One Word Training races — per-race (per-session) time-series log.
 * Firestore: users/{uid}/oneWordRaces/{autoId}
 *
 * Each completed race writes one document here (the data source for the completion
 * charts) and updates the aggregate record in oneWordRecordsService in the same call.
 */

import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { describeFirestoreError } from '@/firebase/firestore';
import { FIREBASE_COLLECTIONS } from '@/types/firebase';
import {
  OneWordRecord,
  getOneWordRecord,
  writeOneWordRecord,
  mergeRaceIntoRecord,
  normalizeWordKey,
} from './oneWordRecordsService';

export type RaceRange = 'day' | 'week' | 'month' | 'all';

export interface OneWordRace {
  id?: string;
  wordKey: string;
  word: string;
  startedAt: number;
  completedAt: number;
  lapsCompleted: number;   // total finished laps (any quality) — "Best Race"
  lapsClean: number;       // laps finished without any error
  bestLapMs: number;       // fastest clean lap this race — "Best Lap"
  bestStreak: number;      // longest clean streak this race — "Best Flow"
  avgLapMs: number;
  totalTimeMs: number;
  letterMistakes: Record<string, number>; // mistakes per letter this race
  totalMistakes: number;
}

export interface RaceInput {
  startedAt: number;
  completedAt: number;
  lapsCompleted: number;
  lapsClean: number;
  bestLapMs: number;
  bestStreak: number;
  avgLapMs: number;
  totalTimeMs: number;
  letterMistakes: Record<string, number>;
}

const RANGE_MS: Record<Exclude<RaceRange, 'all'>, number> = {
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
};

/**
 * Persist one finished race: writes the race document AND updates the aggregate
 * record (best/avg/running sums + cumulative letter mistakes). Returns the merged
 * aggregate record so the UI can refresh its "record" comparisons immediately.
 */
export const saveOneWordRace = async (
  uid: string,
  word: string,
  race: RaceInput
): Promise<OneWordRecord | null> => {
  if (!uid || !word) return null;
  const wordKey = normalizeWordKey(word);
  const totalMistakes = Object.values(race.letterMistakes).reduce((a, b) => a + b, 0);

  const raceDoc: OneWordRace = {
    wordKey,
    word: word.trim(),
    startedAt: race.startedAt,
    completedAt: race.completedAt,
    lapsCompleted: race.lapsCompleted,
    lapsClean: race.lapsClean,
    bestLapMs: race.bestLapMs,
    bestStreak: race.bestStreak,
    avgLapMs: race.avgLapMs,
    totalTimeMs: race.totalTimeMs,
    letterMistakes: race.letterMistakes,
    totalMistakes,
  };

  // Write the time-series race doc (best-effort; failure must not break the aggregate).
  try {
    await addDoc(
      collection(db, FIREBASE_COLLECTIONS.USERS, uid, FIREBASE_COLLECTIONS.ONE_WORD_RACES),
      { ...raceDoc, createdAt: serverTimestamp() }
    );
  } catch (e) {
    console.warn('oneWordRaces: write failed', e);
    describeFirestoreError(`saveOneWordRace ${wordKey}`, e);
  }

  // Update the aggregate record.
  const prev = await getOneWordRecord(uid, word);
  const merged = mergeRaceIntoRecord(prev, {
    bestLapMs: race.bestLapMs,
    bestStreak: race.bestStreak,
    lapsCompleted: race.lapsCompleted,
    totalTimeMs: race.totalTimeMs,
    letterMistakes: race.letterMistakes,
  });
  await writeOneWordRecord(uid, word, merged);
  return merged;
};

/**
 * Fetch races for the completion / training-statistics charts.
 * Pass a wordKey to scope to one word, or omit for all words ("All" view).
 */
export const getOneWordRaces = async (
  uid: string,
  options: { wordKey?: string; range?: RaceRange } = {}
): Promise<OneWordRace[]> => {
  if (!uid) return [];
  const { wordKey, range = 'all' } = options;

  try {
    const colRef = collection(db, FIREBASE_COLLECTIONS.USERS, uid, FIREBASE_COLLECTIONS.ONE_WORD_RACES);
    const constraints: any[] = [];
    if (wordKey) constraints.push(where('wordKey', '==', wordKey));
    constraints.push(orderBy('completedAt', 'asc'));

    const snap = await getDocs(query(colRef, ...constraints));
    let races = snap.docs.map((d) => ({ id: d.id, ...(d.data() as OneWordRace) }));

    if (range !== 'all') {
      const cutoff = Date.now() - RANGE_MS[range];
      races = races.filter((r) => r.completedAt >= cutoff);
    }
    return races;
  } catch (e) {
    console.warn('oneWordRaces: query failed', e);
    describeFirestoreError(`getOneWordRaces ${wordKey ?? 'all'}`, e);
    return [];
  }
};
